import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import {
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
} from "../../xml/transmitterData";

// 1042-S (Foreign Person's U.S. Source Income Subject to Withholding). Unlike the 1099 forms, 1042-S
// uses the IRSubmission3 envelope: a IRSubmission3Header carrying the withholding agent as
// Form1042TIssuerDetail (parent form 1042-T), then Form1042SDetail records with Chapter 3/4 groups,
// income/exemption codes, and foreign recipient details. Columns map 1:1 from the legacy BOX* fields.

// ── helpers ──────────────────────────────────────────────────────────────────
const clean = (v: any) => String(v ?? "").trim();
const digits = (v: any) => clean(v).replace(/\D/g, "");
// 1042-S amounts are USAmountNNType = whole, non-negative dollars (NO cents).
const nAmt = (v: any) => Math.max(0, Math.round(parseFloat(clean(v)) || 0));
const amt = (v: any) => String(nAmt(v));
// TaxRt is a ratio 0..0.9999 (4dp), but the legacy rate is stored as a percent ("30.00" = 30%).
const rate = (v: any) => (Math.min(0.9999, Math.max(0, (parseFloat(clean(v)) || 0) / 100))).toFixed(4);
const bool = (v: any) => (clean(v) === "1" || /^y/i.test(clean(v)) ? "1" : "0");
const pad2 = (v: any) => {
  const d = digits(v);
  return d ? d.padStart(2, "0").slice(-2) : "";
};

/** Split a single "First Last" recipient name into IRS PersonName parts. */
function personName(full: string) {
  const parts = clean(full).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { PersonFirstNm: "Unknown", PersonLastNm: "Unknown" };
  if (parts.length === 1) return { PersonFirstNm: parts[0], PersonLastNm: parts[0] };
  return { PersonFirstNm: parts[0], PersonLastNm: parts.slice(1).join(" ") };
}

/** Build a MailingAddressGrp (USAddress for US, else ForeignAddress). */
function address(a: {
  line1?: any; line2?: any; city?: any; state?: any; province?: any; zip?: any; country?: any; foreign?: any;
}) {
  const country = clean(a.country).toUpperCase();
  const isForeign = clean(a.foreign) === "1" || (country && country !== "US");
  const line1 = clean(a.line1) || "NA";
  if (isForeign) {
    return {
      ForeignAddress: {
        AddressLine1Txt: line1,
        ...(clean(a.line2) && { AddressLine2Txt: clean(a.line2) }),
        ...(clean(a.city) && { CityNm: clean(a.city) }),
        ...(clean(a.province || a.state) && { ProvinceOrStateNm: clean(a.province || a.state) }),
        CountryCd: country || "CA",
        ...(clean(a.zip) && { ForeignPostalCd: clean(a.zip) }),
      },
    };
  }
  return {
    USAddress: {
      AddressLine1Txt: line1,
      ...(clean(a.line2) && { AddressLine2Txt: clean(a.line2) }),
      CityNm: clean(a.city) || "NA",
      StateAbbreviationCd: clean(a.state) || "NY",
      ZIPCd: digits(a.zip).padStart(5, "0").slice(0, 5) || "00000",
    },
  };
}

export async function generate1042SXml(
  ctx: EngineContext,records: any[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No 1042-S records found or records input is invalid.");
  }

  const transmitterData = ctx.transmitter as any;
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: false,
  });

  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  const testInd = transmitterData.testFileIndicator === "T";
  // IRS test rule: with TestCd='T' every TIN/EIN's first 3 digits must be "000". Keep the last 6 so the
  // value is still a valid 9-digit id, just masked (matches the 1099 forms' test-mode TIN handling).
  const tin = (v: any) => {
    const d = digits(v) || "000000000";
    return testInd ? "000" + d.padStart(9, "0").slice(-6) : d;
  };

  // ── per-record Form1042SDetail ──
  const details = records.map((r) => {
    const rid = (r.id ?? 0).toString();
    const chapter = clean(r.BOX3_CHAPTER_INDICATOR) || "3";

    const detail: any = {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
      RecordId: rid,
      // The IRS FormId pattern enumerates leading-zero counts 0-7 and 9 but omits 8, so a
      // zero-padded id of 10..99 ("0000000010") matches no alternative and fails XSD validation.
      // Offsetting every id by a constant keeps FormId unique per record while guaranteeing a
      // value >= 100, which always lands in the accepted 0-7 leading-zero range.
      FormId: String(Number(rid) + 100).padStart(10, "0").slice(-10),
      AmendedInd: "0",
      IncomeTypeCd: pad2(r.BOX1_INCOME_CODE) || "23",
      GrossIncomeAmt: amt(r.BOX2_GROSS_INCOME),
      ChapterTypeCd: chapter,
    };

    // Chapter 3 group (optional) — only when a chapter-3 rate/exemption is present.
    if (clean(r.BOX3B_TAX_RATE) || clean(r.BOX3A_EXEMPTION_CODE)) {
      detail.Chapter3Grp = {
        ...(pad2(r.BOX3A_EXEMPTION_CODE) && { ExemptionCd: pad2(r.BOX3A_EXEMPTION_CODE) }),
        TaxRt: rate(r.BOX3B_TAX_RATE),
      };
    }
    // Chapter 4 group (REQUIRED).
    detail.Chapter4Grp = {
      ExemptionCd: pad2(r.BOX4A_EXEMPTION_CODE) || "15",
      TaxRt: rate(r.BOX4B_TAX_RATE),
    };

    if (clean(r.BOX5_WITHHOLDING_ALLOWANCE)) detail.WithholdingAllowanceAmt = amt(r.BOX5_WITHHOLDING_ALLOWANCE);
    if (clean(r.BOX6_NET_INCOME)) detail.NetIncomeAmt = amt(r.BOX6_NET_INCOME);
    detail.FederalIncomeTaxWithheldAmt = amt(r.BOX7A_FEDERAL_TAX_WITHHELD);
    detail.TaxNotDepositPerEscrowProcInd = bool(r.BOX7B_TAX_NOT_DEP_W_IRS);
    detail.SubsqYrWithholdingPrtshpIntInd = bool(r.BOX7C_WITHHOLD_RES_PARTNERSHIP_INRST);
    detail.QIOrWPOrWTRevisingReportingInd = bool(r.BOX7D_QUALIFIED_INTERMEDIARY);
    detail.OtherAgentsTaxWithheldAmt = amt(r.BOX8_TAX_WITHHELD_OTHER_AGENTS);
    if (clean(r.BOX9_AMOUNT_REPAID_RECIPIENT)) detail.RecipientRepaidAmt = amt(r.BOX9_AMOUNT_REPAID_RECIPIENT);
    // IRS cross-check: Total Withholding Credit MUST equal OtherAgents + FederalWithheld - RecipientRepaid.
    detail.TotalTaxWithholdingCreditAmt = String(Math.max(0,
      nAmt(r.BOX8_TAX_WITHHELD_OTHER_AGENTS) + nAmt(r.BOX7A_FEDERAL_TAX_WITHHELD) - nAmt(r.BOX9_AMOUNT_REPAID_RECIPIENT)));
    if (clean(r.BOX11_TAX_PAID_WITHHOLDING_AGENT)) detail.WithholdingAgentTaxPaidAmt = amt(r.BOX11_TAX_PAID_WITHHOLDING_AGENT);

    // Recipient detail (Box 13) — foreign person.
    const nm = personName(r.BOX13A_RECIPIENT_NAME1);
    detail.Form1042SRecipientDetail = {
      PersonName: nm,
      ...(clean(r.BOX13B_RECIPIENT_COUNTRY_CODE) && { CountryCd: clean(r.BOX13B_RECIPIENT_COUNTRY_CODE).toUpperCase() }),
      MailingAddressGrp: address({
        line1: r.BOX13C_RECIPIENT_ADDRESS1, line2: r.BOX13C_RECIPIENT_ADDRESS2,
        city: r.BOX13D_RECIPIENT_CITY, state: r.BOX13D_RECIPIENT_STATE, province: r.BOX13D_RECIPIENT_PROVINCE,
        zip: r.BOX13D_RECIPIENT_ZIP, country: r.BOX13B_RECIPIENT_COUNTRY_CODE || r.BOX13D_RECIPIENT_COUNTRY,
        foreign: r.BOX13D_RECIPIENT_FOREIGN_INDICATOR,
      }),
      ...(digits(r.BOX13E_RECIPIENT_US_TIN) && { TIN: tin(r.BOX13E_RECIPIENT_US_TIN), TINSubmittedTypeCd: "INDIVIDUAL_TIN" }),
      ...(pad2(r.BOX13F_RECIPIENT_CH3_STATUS_CODE) && { Chapter3StatusCd: pad2(r.BOX13F_RECIPIENT_CH3_STATUS_CODE) }),
      ...(pad2(r.BOX13G_RECIPIENT_CH4_STATUS_CODE) && { Chapter4StatusCd: pad2(r.BOX13G_RECIPIENT_CH4_STATUS_CODE) }),
      ...(clean(r.BOX13I_RECIPIENTS_FOREIGN_TIN) && { FTIN: clean(r.BOX13I_RECIPIENTS_FOREIGN_TIN) }),
      ...(pad2(r.BOX13J_RECIPIENT_LOB_CODE) && { LOBCd: pad2(r.BOX13J_RECIPIENT_LOB_CODE) }),
    };

    if (clean(r.BOX13K_RECIPIENTS_ACCT_NO)) detail.RecipientAccountNum = clean(r.BOX13K_RECIPIENTS_ACCT_NO);
    // IRS rule: a non-zero OtherAgentsTaxWithheldAmt (Box 8) REQUIRES Primary Withholding Agent (Box 14).
    if (nAmt(r.BOX8_TAX_WITHHELD_OTHER_AGENTS) > 0) {
      detail.PrimaryWithholdingAgentGrp = {
        BusinessName: { BusinessNameLine1Txt: clean(r.BOX14A_PRIMARY_WITHHOLDING_AGENT_NAME1) || "Primary Withholding Agent" },
        EIN: tin(r.BOX14B_PRIMARY_WITHHOLDING_AGENT_EIN),
      };
    }
    detail.ProRataBasisInd = bool(r.BOX15_PRO_RATA_BASIS_REPORTING_INDICATOR);

    // State tax group (Box 17) — optional.
    if (clean(r.BOX17A_STATE_INCOME_TAX_WITHHELD) || clean(r.BOX17C_NAME_OF_STATE)) {
      detail.Form1042SStateTaxGrp = {
        ...(clean(r.BOX17A_STATE_INCOME_TAX_WITHHELD) && { StateTaxWithheldAmt: amt(r.BOX17A_STATE_INCOME_TAX_WITHHELD) }),
        ...(clean(r.BOX17B_PAYERS_STATE_TAX_NO) && { StateIdNum: clean(r.BOX17B_PAYERS_STATE_TAX_NO) }),
        StateAbbreviationCd: clean(r.BOX17C_NAME_OF_STATE) || "NY",
      };
    }
    return detail;
  });

  // ── withholding agent (Form1042TIssuerDetail) from the first record's Box 12 ──
  const w = records[0];
  const waForeign = clean(w.BOX12I_WITHHOLDING_AGENT_FOREIGN_INDICATOR) === "1" ||
    (clean(w.BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE).toUpperCase() &&
     clean(w.BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE).toUpperCase() !== "US");
  const issuerDetail: any = {
    ForeignEntityInd: waForeign ? "1" : "0",
    EIN: tin(w.BOX12A_WITHHOLDING_AGENT_EIN),
    TINSubmittedTypeCd: "BUSINESS_TIN",
    Chapter3StatusCd: pad2(w.BOX12B_WITHHOLDING_AGENT_CH3_STATUS_CODE) || "15",
    Chapter4StatusCd: pad2(w.BOX12C_WITHHOLDING_AGENT_CH4_STATUS_CODE) || "16",
    BusinessName: { BusinessNameLine1Txt: clean(w.BOX12D_WITHHOLDING_AGENT_NAME1) || "Withholding Agent" },
    ...(clean(w.BOX12E_WITHHOLDING_AGENTS_GIIN) && { GIIN: clean(w.BOX12E_WITHHOLDING_AGENTS_GIIN) }),
    CountryCd: clean(w.BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE).toUpperCase() || "US",
    ...(clean(w.BOX12G_WITHHOLDING_AGENT_FOREIGN_TIN) && { FTIN: clean(w.BOX12G_WITHHOLDING_AGENT_FOREIGN_TIN) }),
    MailingAddressGrp: address({
      line1: w.BOX12H_WITHHOLDING_AGENT_ADDRESS1, line2: w.BOX12H_WITHHOLDING_AGENT_ADDRESS2,
      city: w.BOX12I_WITHHOLDING_AGENT_CITY, state: w.BOX12I_WITHHOLDING_AGENT_STATE_CODE,
      province: w.BOX12I_WITHHOLDING_AGENT_PROVINCE_CODE, zip: w.BOX12I_WITHHOLDING_AGENT_ZIP,
      country: w.BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE, foreign: w.BOX12I_WITHHOLDING_AGENT_FOREIGN_INDICATOR,
    }),
  };

  const submissionId = generateUniqueSubmissionId();
  const transmissionId = generateUniqueTransmissionId(ctx.irs.tcc);

  const sum = (f: (r: any) => number) =>
    String(Math.max(0, Math.round(records.reduce((s, r) => s + (f(r) || 0), 0))));

  const xmlStructure = {
    "?xml": { "@_version": "1.0", "@_encoding": "UTF-8" },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation": "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: transmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: "1",
        TotalRecipientFormCnt: details.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission3Grp: [
        {
          IRSubmission3Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            ChapterTypeCd: clean(w.BOX3_CHAPTER_INDICATOR) || "3",
            FinalReturnInd: "0",
            ProRataBasisInd: bool(w.BOX15_PRO_RATA_BASIS_REPORTING_INDICATOR),
            TotalReportedRcpntFormCnt: details.length.toString(),
            SubsqYrWithholdingPrtshpIntInd: "0",
            QIOrWPOrWTRevisingReportingInd: "0",
            FormTypeCd: "1042S",
            ParentFormTypeCd: "1042T",
            Form1042TIssuerDetail: issuerDetail,
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && { ContactPersonNm: transmitterData.contactName }),
              ...(transmitterData.contactTelephoneNumber && { ContactPhoneNum: transmitterData.contactTelephoneNumber }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
            },
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0],
              ...(transmitterData.contactName && { JuratPersonTitleTxt: "CFO" }),
              ...(transmitterData.contactName && { PersonNm: transmitterData.contactName }),
            },
            Form1042STotalAmtGrp: {
              FederalIncomeTaxWithheldAmt: sum((r) => parseFloat(clean(r.BOX7A_FEDERAL_TAX_WITHHELD)) || 0),
              GrossIncomeAmt: sum((r) => parseFloat(clean(r.BOX2_GROSS_INCOME)) || 0),
            },
          },
          // Per XSD IRSubmission3GrpType: Form1042SDetail is a direct sibling of the header (no
          // IRSubmission3Detail wrapper, unlike the 1099 forms' IRSubmission1Detail).
          Form1042SDetail: details,
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating 1042-S XML:", error);
    throw new Error(`Failed to generate 1042-S XML: ${error.message}`);
  }
}
